import {
    type ProviderRow,
    type ProviderSchema,
    SECRET_COLUMN,
    type SecretName,
    secretNamesOf,
} from '@/domain/providerConfig';

import type { UnreadableStored } from './ProviderSchemaField';

export interface SecretField {
    name: SecretName;
    labelKey: string;
    /** The site needs it; a pen's own box is an override and never required. */
    required: boolean;
}

export function secretFieldsOf(schema: ProviderSchema): SecretField[] {
    return secretNamesOf(schema).map((name) => {
        const field = schema.fields.find((entry) => entry.key === SECRET_COLUMN[name]);
        return {
            name,
            labelKey: field?.labelKey ?? `providerConfig.fields.${name}`,
            required: field?.required ?? false,
        };
    });
}

/** The SCADA editor's own label for each entry part the server can name. */
const SCADA_PART_LABELS: Readonly<Record<string, string>> = {
    item_id: 'providerConfig.scada.itemId',
    signal: 'providerConfig.scada.signal',
    feed_line: 'providerConfig.scada.feedLine',
};

/** A SCADA entry part's place, e.g. "[1].item_id" as entry 2, the item id box. */
function entryOf(suffix: string): UnreadableStored['entry'] {
    const match = /^\[(\d+)\](?:\.(.+))?$/.exec(suffix);
    if (match === null) return null;
    const part = match[2] === undefined ? null : (SCADA_PART_LABELS[match[2]] ?? null);
    return { number: Number(match[1]) + 1, partKey: part };
}

/**
 * What the row stores at `key` that the field cannot read, for the field to say so: its own
 * value, or for a SCADA list each entry or entry part the editor shows blank.
 */
export function unreadableAt(
    row: Pick<ProviderRow, 'unreadable'> | null,
    key: string
): UnreadableStored[] {
    return (row?.unreadable ?? []).flatMap((candidate): UnreadableStored[] => {
        const { type, value, blocked } = candidate;
        if (candidate.key === key) return [{ type, value, blocked, entry: null }];
        if (!candidate.key.startsWith(`${key}[`)) return [];
        const entry = entryOf(candidate.key.slice(key.length));
        return entry === null ? [] : [{ type, value, blocked, entry }];
    });
}
